import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOpenPaymentItems, mapPaymentError } from '../../api/endpoints/payments';
import { usePayment, useVerifyPaymentClaim, useRejectPaymentClaim } from './hooks';
import { usePaymentProof } from './usePaymentProof';
import { RecordReceiptDialog } from './RecordReceiptDialog';
import { ReasonDialog } from './ReasonDialog';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
export function VerifyClaimFlow({
  paymentId,
  onClose,
}: {
  paymentId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = usePayment(paymentId);
  const payment = detail.data;
  const verify = useVerifyPaymentClaim();
  const reject = useRejectPaymentClaim();
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const proof = usePaymentProof(paymentId, Boolean(payment?.proofFileKey), false);
  const items = useQuery({
    queryKey: ['payments', 'claim-items', paymentId, payment?.counterpartyUserId],
    enabled: payment?.status === 'CLAIMED',
    queryFn: async () => {
      const first = await getOpenPaymentItems('IN', payment!.counterpartyUserId);
      const claimLines = payment!.allocations.filter((a) => a.status === 'CLAIMED');
      const enriched = claimLines.map((line) => {
        if (line.currentOutstanding == null)
          throw new Error('Current invoice balance is unavailable. Refresh claim details.');
        const existing = first.orders.find((order) => order.shipmentId === line.shipmentId);
        return existing
          ? { ...existing, outstandingAmount: line.currentOutstanding }
          : {
              shipmentId: line.shipmentId,
              publicUuid: '',
              route: line.route ?? '',
              vehicleNumber: line.vehicleNumber,
              status: '',
              amountDue: line.currentOutstanding,
              settledAmount: '0.00',
              outstandingAmount: line.currentOutstanding,
              invoiceNumber: line.invoiceNumber ?? null,
              invoiceDueDate: null,
              advanceRemaining: '0.00',
              balanceRemaining: '0.00',
              partnerRemaining: '0.00',
              advanceAvailable: '0.00',
              advanceUnavailableReason: null,
            };
      });
      return {
        ...first,
        orders: [
          ...new Map(
            [...first.orders, ...enriched].map((order) => [order.shipmentId, order]),
          ).values(),
        ],
      };
    },
  });
  const claim = useMemo(
    () =>
      payment
        ? {
            amount: payment.amount,
            reference: payment.reference ?? '',
            paidAt: payment.paidAt ?? payment.createdAt,
            proofUrl: proof.url,
            proofFeedback:
              proof.state === 'loading' ? (
                <p role="status">{t('payments.verifyClaimFlow.loadingProof')}</p>
              ) : (
                <>
                  <p role="alert">{t('payments.verifyClaimFlow.proofUnavailable')}</p>
                  <Button variant="secondary" size="sm" onClick={() => void proof.load()}>
                    {t('payments.verifyClaimFlow.retryProof')}
                  </Button>
                </>
              ),
            allocations: payment.allocations.filter((a) => a.status === 'CLAIMED'),
          }
        : undefined,
    [payment, proof, t],
  );
  if (rejecting)
    return (
      <ReasonDialog
        title={t('payments.verifyClaimFlow.rejectTitle')}
        actionLabel={t('payments.verifyClaimFlow.rejectClaim')}
        help="verify-claim"
        submitting={reject.isPending}
        error={error}
        onCancel={() => setRejecting(false)}
        onSubmit={async (reason) => {
          if (reject.isPending) return;
          setError(null);
          try {
            await reject.mutateAsync({ paymentId, reason });
            onClose();
          } catch (cause) {
            setError(mapPaymentError(cause));
          }
        }}
      />
    );
  if (!payment || !items.data || payment.status !== 'CLAIMED')
    return (
      <div className={styles.scrim}>
        <section
          role="dialog"
          aria-modal="true"
          aria-label={t('payments.verifyClaimFlow.title')}
          className={styles.dialog}
        >
          <div className={styles.body}>
            {detail.isError || items.isError ? (
              <p role="alert">
                {items.error instanceof Error ? items.error.message : mapPaymentError(detail.error)}
              </p>
            ) : payment && payment.status !== 'CLAIMED' ? (
              <p role="alert">{t('payments.verifyClaimFlow.alreadyDecided')}</p>
            ) : (
              <p role="status">{t('payments.verifyClaimFlow.loading')}</p>
            )}
            {(detail.isError || items.isError) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void detail.refetch();
                  void items.refetch();
                }}
              >
                {t('payments.verifyClaimFlow.retry')}
              </Button>
            )}
            {payment?.status === 'CLAIMED' && (
              <Button variant="danger" size="sm" onClick={() => setRejecting(true)}>
                {t('payments.verifyClaimFlow.rejectClaim')}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.close')}
            </Button>
          </div>
        </section>
      </div>
    );
  return (
    <>
      <RecordReceiptDialog
        key={payment.id}
        openItems={items.data}
        shipperName={payment.claimedByName ?? `Shipper #${payment.counterpartyUserId}`}
        claim={claim}
        onClose={onClose}
        submitting={verify.isPending}
        onRejectClaim={() => setRejecting(true)}
        onSubmit={async (request) => {
          try {
            await verify.mutateAsync({
              paymentId,
              request: {
                amount: request.amount,
                method: request.method,
                allocations: request.allocations,
                note: request.note,
                ...(request.proofSafeToShare ? { proofSafeToShare: true } : {}),
              },
            });
            onClose();
          } catch (cause) {
            throw new Error(mapPaymentError(cause));
          }
        }}
      />
    </>
  );
}
